<template>
  <div
    v-if="available"
    :style="{
      position: `relative`,
      backgroundImage: `url(${preview.previewMask.src})`,
      width: `${previewWidth * scale}px`,
      height: `${previewHeight * scale}px`,
      backgroundRepeat: `no-repeat`,
      backgroundSize: `100% 100%`,
    }"
  >
    <div :style="styleMask" class="mask">
      <div class="mask-matrix mask-matrix--image" :style="styleStamp"></div>
    </div>
    <div class="mask-matrix mask-matrix__border" :style="styleStamp"></div>
    <slot></slot>
  </div>
</template>

<script>
export default {
  props: {
    preview: Object,
    scale: Number,
    previewWidth: Number,
    previewHeight: Number,
  },
  computed: {
    available() {
      return !!this.previewWidth && !!this.previewHeight;
    },
    styleMask() {
      const timestamp = 0;
      const { src } = this.preview.previewMask;
      if (!src) return false;
      return {
        "-webkit-mask-image": `url(${src}?t=${timestamp})`,
        "-o-mask-image": `url(${src}?t=${timestamp})`,
        "-moz-mask-image": `url(${src}?t=${timestamp})`,
        "mask-image": `url(${src}?t=${timestamp})`,
      };
    },
    /* eslint-disable */
    styleStamp() {
      const { previewWidth, previewHeight } = this;
      const { stampX1, stampY1, stampX2, stampY2, stampX3, stampY3, stampX4, stampY4, positionX, positionY } = this.preview;
      // Apply local scale and multiple for zoom scale
      const t = this.transform2d(
        previewWidth * this.scale,
        previewHeight * this.scale,
        (stampX1 + positionX) * this.scale,
        (stampY1 + positionY) * this.scale,
        (stampX2 + positionX) * this.scale,
        (stampY2 + positionY) * this.scale,
        (stampX3 + positionX) * this.scale,
        (stampY3 + positionY) * this.scale,
        (stampX4 + positionX) * this.scale,
        (stampY4 + positionY) * this.scale
      );
      const transform = "matrix3d(" + t.join(", ") + ")";
      return {
        transform,
        "-o-transform": transform,
        "-ms-transform": transform,
        "-moz-transform": transform,
        "-webkit-transform": transform,
      };
    },
  },
  methods: {
    adj(m) {
      // Compute the adjugate of m
      return [
        m[4] * m[8] - m[5] * m[7],
        m[2] * m[7] - m[1] * m[8],
        m[1] * m[5] - m[2] * m[4],
        m[5] * m[6] - m[3] * m[8],
        m[0] * m[8] - m[2] * m[6],
        m[2] * m[3] - m[0] * m[5],
        m[3] * m[7] - m[4] * m[6],
        m[1] * m[6] - m[0] * m[7],
        m[0] * m[4] - m[1] * m[3],
      ];
    },
    multmm(a, b) {
      // multiply two matrices
      var c = Array(9);
      for (var i = 0; i != 3; ++i) {
        for (var j = 0; j != 3; ++j) {
          var cij = 0;
          for (var k = 0; k != 3; ++k) {
            cij += a[3 * i + k] * b[3 * k + j];
          }
          c[3 * i + j] = cij;
        }
      }
      return c;
    },
    multmv(m, v) {
      // multiply matrix and vector
      return [
        m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
        m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
        m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
      ];
    },
    pdbg(m, v) {
      var r = this.multmv(m, v);
      return r + " (" + r[0] / r[2] + ", " + r[1] / r[2] + ")";
    },
    basisToPoints(x1, y1, x2, y2, x3, y3, x4, y4) {
      var m = [x1, x2, x3, y1, y2, y3, 1, 1, 1];
      var v = this.multmv(this.adj(m), [x4, y4, 1]);
      return this.multmm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
    },
    general2DProjection(
      x1s, y1s, x1d, y1d,
      x2s, y2s, x2d, y2d,
      x3s, y3s, x3d, y3d,
      x4s, y4s, x4d, y4d
    ) {
      var s = this.basisToPoints(x1s, y1s, x2s, y2s, x3s, y3s, x4s, y4s);
      var d = this.basisToPoints(x1d, y1d, x2d, y2d, x3d, y3d, x4d, y4d);
      return this.multmm(d, this.adj(s));
    },
    project(m, x, y) {
      var v = this.multmv(m, [x, y, 1]);
      return [v[0] / v[2], v[1] / v[2]];
    },
    transform2d(w, h, x1, y1, x2, y2, x3, y3, x4, y4) {
      var t = this.general2DProjection(0, 0, x1, y1, w, 0, x2, y2, 0, h, x3, y3, w, h, x4, y4);
      for(let i = 0; i != 9; ++i) t[i] = t[i]/t[8];
      t = [t[0], t[3], 0, t[6],
          t[1], t[4], 0, t[7],
          0   , 0   , 1, 0   ,
          t[2], t[5], 0, t[8]];
      return t;
    },
    /* eslint-enable */
  },
};
</script>

<style lang="scss" scoped>
.mask-matrix {
  width: 100%;
  height: 100%;
  transform-origin: 0 0;
  transform-style: preserve-3d;
  background-repeat: no-repeat;
  background-size: 100% 100%;

  &--image {
    background-image: url(https://custom-case-images.s3.amazonaws.com/prisma-render/prod/standard-iphone11/grid-primavera-escarlate/9208113272391218161488314262818600667993308106.png);
  }
}

.mask-matrix__border {
  border: 1px solid #fff;
  position: absolute;
  top: 0;
  left: 0;
}

.mask {
  width: 100%;
  height: 100%;

  overflow: hidden;

  outline: 1px solid transparent;
  box-shadow: 0 0 1px rgba(255, 255, 255, 0);

  mask-size: 100% 100%;
  mask-position: center;
  mask-repeat: no-repeat;

  -webkit-mask-size: 100% 100%;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;

  -o-mask-size: 100% 100%;
  -o-mask-position: center;
  -o-mask-repeat: no-repeat;

  -moz-mask-size: 100% 100%;
  -moz-mask-position: center;
  -moz-mask-repeat: no-repeat;
}
</style>
